export type EmergencyIdentityField = "full_name" | "date_of_birth" | "blood_group";
export type EmergencyContactField = "emergency_contact" | "primary_doctor" | "preferred_hospital" | "insurance";

const IDENTITY_FIELDS: EmergencyIdentityField[] = ["full_name", "date_of_birth", "blood_group"];
const CONTACT_FIELDS: EmergencyContactField[] = ["emergency_contact", "primary_doctor", "preferred_hospital", "insurance"];

export type EmergencyAccessConfig = {
  enabled: boolean;
  identity_fields: EmergencyIdentityField[];
  contact_fields: EmergencyContactField[];
  selected_allergy_ids: string[];
  selected_condition_ids: string[];
  selected_medication_ids: string[];
  updated_at: string | null;
};

export type EmergencyAccessPatch = Partial<{
  identity_fields: EmergencyIdentityField[];
  contact_fields: EmergencyContactField[];
  selected_allergy_ids: string[];
  selected_condition_ids: string[];
  selected_medication_ids: string[];
}>;

export type EmergencyCardEmergencyContact = {
  name: string;
  relationship: string | null;
  primary_mobile_number: string;
  alternate_mobile_number: string | null;
  priority: number | null;
};

export type EmergencyCardPrimaryDoctor = {
  name: string | null;
  speciality: string | null;
  hospital_clinic_name: string | null;
  phone: string | null;
};

export type EmergencyCardHospital = {
  name: string | null;
  address: string | null;
  emergency_number: string | null;
  ambulance_contact_number: string | null;
};

export type EmergencyCardInsurance = {
  insurance_company: string | null;
  insurance_company_contact_phone: string | null;
};

export type EmergencyCardProfile = {
  full_name?: string;
  date_of_birth?: string;
  age?: number;
  blood_group?: string;
  emergency_contacts?: EmergencyCardEmergencyContact[];
  primary_doctor?: EmergencyCardPrimaryDoctor;
  preferred_hospitals?: { primary?: EmergencyCardHospital; alternate?: EmergencyCardHospital };
  insurance?: EmergencyCardInsurance;
};

export type EmergencyCardAllergy = {
  allergy_id: string;
  substance_name: string | null;
  category: string | null;
  reaction: string | null;
  severity: string | null;
  status: string | null;
};

export type EmergencyCardCondition = {
  condition_id: string;
  condition_name: string | null;
  status: string | null;
};

export type EmergencyCardMedication = {
  medication_id: string;
  name: string | null;
  medication_type: string | null;
  dose_text: string | null;
  form: string | null;
  strength: string | null;
  instructions: string | null;
  status: string | null;
};

export type EmergencyCard = {
  profile: EmergencyCardProfile;
  // Omitted entirely (never an empty array) whenever nothing was selected or nothing resolved --
  // mirrors the backend contract exactly, so "not present" always means "nothing to show here".
  allergies?: EmergencyCardAllergy[];
  conditions?: EmergencyCardCondition[];
  medications?: EmergencyCardMedication[];
  updated_at: string | null;
};

export class EmergencyAccessError extends Error {
  constructor(message = "We could not update Emergency Access right now. Please try again.") {
    super(message);
    this.name = "EmergencyAccessError";
  }
}

export class EmergencyAccessNotEnabledError extends EmergencyAccessError {
  constructor() {
    super("Turn on Emergency Access before allowing it on this device.");
    this.name = "EmergencyAccessNotEnabledError";
  }
}

export class EmergencyCardNotFoundError extends Error {
  constructor() {
    super("This Emergency Card is not available.");
    this.name = "EmergencyCardNotFoundError";
  }
}

export class EmergencyCardUnavailableError extends Error {
  constructor() {
    super("The Emergency Card could not be loaded right now. Please try again in a moment.");
    this.name = "EmergencyCardUnavailableError";
  }
}

function textOrNull(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function textOrUndefined(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function normalizeConfig(value: Partial<EmergencyAccessConfig> | undefined): EmergencyAccessConfig {
  return {
    enabled: value?.enabled === true,
    identity_fields: Array.isArray(value?.identity_fields)
      ? value.identity_fields.filter((field): field is EmergencyIdentityField => IDENTITY_FIELDS.includes(field))
      : [],
    contact_fields: Array.isArray(value?.contact_fields)
      ? value.contact_fields.filter((field): field is EmergencyContactField => CONTACT_FIELDS.includes(field))
      : [],
    selected_allergy_ids: Array.isArray(value?.selected_allergy_ids)
      ? value.selected_allergy_ids.filter((id): id is string => typeof id === "string")
      : [],
    selected_condition_ids: Array.isArray(value?.selected_condition_ids)
      ? value.selected_condition_ids.filter((id): id is string => typeof id === "string")
      : [],
    selected_medication_ids: Array.isArray(value?.selected_medication_ids)
      ? value.selected_medication_ids.filter((id): id is string => typeof id === "string")
      : [],
    updated_at: typeof value?.updated_at === "string" ? value.updated_at : null,
  };
}

function normalizeEmergencyContact(value: unknown): EmergencyCardEmergencyContact | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  const name = textOrNull(record.name);
  const primaryMobileNumber = textOrNull(record.primary_mobile_number);
  if (!name || !primaryMobileNumber) {
    return null;
  }
  return {
    name,
    relationship: textOrNull(record.relationship),
    primary_mobile_number: primaryMobileNumber,
    alternate_mobile_number: textOrNull(record.alternate_mobile_number),
    priority: typeof record.priority === "number" && Number.isFinite(record.priority) ? record.priority : null,
  };
}

function normalizeCardPrimaryDoctor(value: unknown): EmergencyCardPrimaryDoctor | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const doctor = {
    name: textOrNull(record.name),
    speciality: textOrNull(record.speciality),
    hospital_clinic_name: textOrNull(record.hospital_clinic_name),
    phone: textOrNull(record.phone),
  };
  return Object.values(doctor).some(Boolean) ? doctor : undefined;
}

function normalizeCardHospital(value: unknown): EmergencyCardHospital | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const hospital = {
    name: textOrNull(record.name),
    address: textOrNull(record.address),
    emergency_number: textOrNull(record.emergency_number),
    ambulance_contact_number: textOrNull(record.ambulance_contact_number),
  };
  return Object.values(hospital).some(Boolean) ? hospital : undefined;
}

function normalizeCardInsurance(value: unknown): EmergencyCardInsurance | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const insurance = {
    insurance_company: textOrNull(record.insurance_company),
    insurance_company_contact_phone: textOrNull(record.insurance_company_contact_phone),
  };
  return Object.values(insurance).some(Boolean) ? insurance : undefined;
}

function normalizeCardProfile(value: unknown): EmergencyCardProfile {
  if (!value || typeof value !== "object") {
    return {};
  }
  const record = value as Record<string, unknown>;
  const hospitalsRaw =
    record.preferred_hospitals && typeof record.preferred_hospitals === "object"
      ? (record.preferred_hospitals as Record<string, unknown>)
      : null;
  const primaryHospital = hospitalsRaw ? normalizeCardHospital(hospitalsRaw.primary) : undefined;
  const alternateHospital = hospitalsRaw ? normalizeCardHospital(hospitalsRaw.alternate) : undefined;
  const emergencyContacts = Array.isArray(record.emergency_contacts)
    ? record.emergency_contacts.map(normalizeEmergencyContact).filter((contact): contact is EmergencyCardEmergencyContact => Boolean(contact))
    : undefined;

  return {
    full_name: textOrUndefined(record.full_name),
    date_of_birth: textOrUndefined(record.date_of_birth),
    age: typeof record.age === "number" && Number.isFinite(record.age) ? record.age : undefined,
    blood_group: textOrUndefined(record.blood_group),
    emergency_contacts: emergencyContacts && emergencyContacts.length > 0 ? emergencyContacts : undefined,
    primary_doctor: normalizeCardPrimaryDoctor(record.primary_doctor),
    preferred_hospitals: primaryHospital || alternateHospital ? { primary: primaryHospital, alternate: alternateHospital } : undefined,
    insurance: normalizeCardInsurance(record.insurance),
  };
}

function normalizeAllergy(value: unknown): EmergencyCardAllergy | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  const allergyId = textOrNull(record.allergy_id);
  if (!allergyId) {
    return null;
  }
  return {
    allergy_id: allergyId,
    substance_name: textOrNull(record.substance_name),
    category: textOrNull(record.category),
    reaction: textOrNull(record.reaction),
    severity: textOrNull(record.severity),
    status: textOrNull(record.status),
  };
}

function normalizeCondition(value: unknown): EmergencyCardCondition | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  const conditionId = textOrNull(record.condition_id);
  if (!conditionId) {
    return null;
  }
  return { condition_id: conditionId, condition_name: textOrNull(record.condition_name), status: textOrNull(record.status) };
}

function normalizeCardMedication(value: unknown): EmergencyCardMedication | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  const medicationId = textOrNull(record.medication_id);
  if (!medicationId) {
    return null;
  }
  return {
    medication_id: medicationId,
    name: textOrNull(record.name),
    medication_type: textOrNull(record.medication_type),
    dose_text: textOrNull(record.dose_text),
    form: textOrNull(record.form),
    strength: textOrNull(record.strength),
    instructions: textOrNull(record.instructions),
    status: textOrNull(record.status),
  };
}

function normalizeCard(value: unknown): EmergencyCard {
  if (!value || typeof value !== "object") {
    return { profile: {}, updated_at: null };
  }
  const record = value as Record<string, unknown>;
  const allergies = Array.isArray(record.allergies)
    ? record.allergies.map(normalizeAllergy).filter((allergy): allergy is EmergencyCardAllergy => Boolean(allergy))
    : undefined;
  const conditions = Array.isArray(record.conditions)
    ? record.conditions.map(normalizeCondition).filter((condition): condition is EmergencyCardCondition => Boolean(condition))
    : undefined;
  const medications = Array.isArray(record.medications)
    ? record.medications.map(normalizeCardMedication).filter((medication): medication is EmergencyCardMedication => Boolean(medication))
    : undefined;

  return {
    profile: normalizeCardProfile(record.profile),
    allergies: allergies && allergies.length > 0 ? allergies : undefined,
    conditions: conditions && conditions.length > 0 ? conditions : undefined,
    medications: medications && medications.length > 0 ? medications : undefined,
    updated_at: textOrNull(record.updated_at),
  };
}

async function request<TResponse>(
  apiBaseUrl: string,
  accessToken: string | undefined,
  path: string,
  method: "GET" | "PATCH" | "POST",
  body?: unknown,
  signal?: AbortSignal,
): Promise<TResponse> {
  if (!accessToken) {
    throw new EmergencyAccessError("Please sign in again to manage Emergency Access.");
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    method,
    signal,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new EmergencyAccessError("This MedoPilot account is scheduled for deletion.");
    }
    if (response.status === 401) {
      throw new EmergencyAccessError("Please sign in again to manage Emergency Access.");
    }
    if (response.status === 400) {
      throw new EmergencyAccessError("Please check your Emergency Access selections and try again.");
    }
    throw new EmergencyAccessError();
  }

  return (await response.json()) as TResponse;
}

async function deviceRequest<TResponse>(
  apiBaseUrl: string,
  accessToken: string | undefined,
  path: string,
  method: "GET" | "POST",
  body?: unknown,
  signal?: AbortSignal,
): Promise<TResponse> {
  if (!accessToken) {
    throw new EmergencyAccessError("Please sign in again to manage Emergency Access.");
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    method,
    signal,
    credentials: "include",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let code: unknown = null;
    try {
      const data = (await response.json()) as { error?: { code?: unknown }; code?: unknown };
      code = data.error?.code ?? data.code;
    } catch {
      code = null;
    }

    if (response.status === 409 && code === "emergency_access_not_enabled") {
      throw new EmergencyAccessNotEnabledError();
    }
    if (response.status === 401) {
      throw new EmergencyAccessError("Please sign in again to manage Emergency Access.");
    }
    throw new EmergencyAccessError();
  }

  return (await response.json()) as TResponse;
}

export function createEmergencyAccessClient(apiBaseUrl: string, getAccessToken: () => string | undefined) {
  return {
    async getConfig(signal?: AbortSignal): Promise<EmergencyAccessConfig> {
      const data = await request<{ emergency_access?: Partial<EmergencyAccessConfig> }>(
        apiBaseUrl,
        getAccessToken(),
        "/v1/me/emergency-access",
        "GET",
        undefined,
        signal,
      );
      return normalizeConfig(data.emergency_access);
    },
    async updateSelections(patch: EmergencyAccessPatch): Promise<EmergencyAccessConfig> {
      const data = await request<{ emergency_access?: Partial<EmergencyAccessConfig> }>(
        apiBaseUrl,
        getAccessToken(),
        "/v1/me/emergency-access",
        "PATCH",
        patch,
      );
      return normalizeConfig(data.emergency_access);
    },
    async enable(): Promise<EmergencyAccessConfig> {
      const data = await request<{ emergency_access?: Partial<EmergencyAccessConfig> }>(
        apiBaseUrl,
        getAccessToken(),
        "/v1/me/emergency-access/enable",
        "POST",
        {},
      );
      return normalizeConfig(data.emergency_access);
    },
    async disable(): Promise<EmergencyAccessConfig> {
      const data = await request<{ emergency_access?: Partial<EmergencyAccessConfig> }>(
        apiBaseUrl,
        getAccessToken(),
        "/v1/me/emergency-access/disable",
        "POST",
        {},
      );
      return normalizeConfig(data.emergency_access);
    },
    async preview(signal?: AbortSignal): Promise<EmergencyCard> {
      const data = await request<{ emergency_card?: unknown }>(
        apiBaseUrl,
        getAccessToken(),
        "/v1/me/emergency-access/preview",
        "GET",
        undefined,
        signal,
      );
      return normalizeCard(data.emergency_card);
    },
    async getDeviceStatus(signal?: AbortSignal): Promise<boolean> {
      return getDeviceEmergencyAccessStatus(apiBaseUrl, signal);
    },
    async enableCurrentDevice(): Promise<boolean> {
      const data = await deviceRequest<{ device_emergency_access?: { enabled?: unknown } }>(
        apiBaseUrl,
        getAccessToken(),
        "/v1/me/emergency-access/device/enable",
        "POST",
        {},
      );
      return data.device_emergency_access?.enabled === true;
    },
    async disableCurrentDevice(): Promise<boolean> {
      await deviceRequest<unknown>(
        apiBaseUrl,
        getAccessToken(),
        "/v1/me/emergency-access/device/disable",
        "POST",
        {},
      );
      return false;
    },
  };
}

export async function getDeviceEmergencyAccessStatus(apiBaseUrl: string, signal?: AbortSignal): Promise<boolean> {
  const response = await fetch(`${apiBaseUrl}/v1/device-emergency-card/status`, {
    method: "GET",
    signal,
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new EmergencyAccessError();
  }

  const data = (await response.json()) as { enabled?: unknown };
  return data.enabled === true;
}

export async function fetchDeviceEmergencyCard(apiBaseUrl: string, signal?: AbortSignal): Promise<EmergencyCard> {
  const response = await fetch(`${apiBaseUrl}/v1/device-emergency-card`, {
    method: "GET",
    signal,
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });

  if (response.status === 404) {
    throw new EmergencyCardNotFoundError();
  }
  if (response.status === 503) {
    throw new EmergencyCardUnavailableError();
  }
  if (!response.ok) {
    throw new EmergencyCardUnavailableError();
  }

  const data = (await response.json()) as { emergency_card?: unknown };
  return normalizeCard(data.emergency_card);
}
